import { AreaChart } from '@zyxantech/axy'
import { Badge, Card, DataTable, PageHeader, Skeleton, Tabs, type Column } from '@zyxantech/tux'
import { StatusLight } from '@zyxantech/wiz'
import { ArrowLeft, ExternalLink } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetGatewayByIdQuery, type Gateway } from '@/api/gateways.api'
import { useGetTagsQuery, type Tag } from '@/api/tags.api'
import { ErrorState } from '@/components/shared/ErrorState'
import { PAGINATION } from '@/constants/pagination'
import { ROUTES } from '@/constants/routes'
import { formatTimestamp } from '@/utils/format'
import { ProtocolTag } from './components/ProtocolTag'
import { gatewayEndpoint, gatewayLightStatus } from './gateway.utils'

type TabId = 'status' | 'tags' | 'activity'

const TABS = [
  { id: 'status', label: 'Status' },
  { id: 'tags', label: 'Tags' },
  { id: 'activity', label: 'Activity' },
]

export function GatewayDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: gateway, isLoading, error, refetch } = useGetGatewayByIdQuery(id, { skip: !id })
  const [tab, setTab] = useState<TabId>('status')

  if (isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton height={28} width="30%" />
        <Skeleton height={160} />
      </div>
    )
  }
  if (error || !gateway) {
    return (
      <div className="p-6">
        <ErrorState error={error} retry={() => void refetch()} />
      </div>
    )
  }

  return (
    <div className="p-6">
      <button
        type="button"
        onClick={() => navigate(ROUTES.GATEWAYS)}
        className="mb-3 flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary"
      >
        <ArrowLeft size={14} /> Gateways
      </button>

      <PageHeader
        title={gateway.name}
        subtitle="Gateway detail"
        actions={
          <StatusLight
            status={gatewayLightStatus(gateway.status)}
            label={gateway.status}
            pulse={gateway.status === 'online'}
          />
        }
      />

      <div className="pt-2">
        <Tabs items={TABS} value={tab} onChange={(t) => setTab(t as TabId)} />
      </div>

      <div className="pt-6">
        {tab === 'status' && <StatusTab gateway={gateway} />}
        {tab === 'tags' && <GatewayTagsTab gatewayId={id} />}
        {tab === 'activity' && <ActivityTab />}
      </div>
    </div>
  )
}

function StatusTab({ gateway }: { gateway: Gateway }) {
  const fields: [string, React.ReactNode][] = [
    ['Status', <ProtocolTag key="p" protocolConfig={gateway.protocolConfig} />],
    ['Endpoint', gatewayEndpoint(gateway.protocolConfig) ?? '—'],
    ['Asset', gateway.assetId ?? '—'],
    ['Last seen', formatTimestamp(gateway.lastSeenAt)],
    ['Created', formatTimestamp(gateway.createdAt)],
    ['Updated', formatTimestamp(gateway.updatedAt)],
  ]
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
      {fields.map(([label, value]) => (
        <Card key={label} padding="sm">
          <div className="text-xs text-text-muted">{label}</div>
          <div className="mt-0.5 truncate font-mono text-sm text-text-primary">{value}</div>
        </Card>
      ))}
    </div>
  )
}

function GatewayTagsTab({ gatewayId }: { gatewayId: string }) {
  const navigate = useNavigate()
  const { data, isLoading, error, refetch } = useGetTagsQuery({
    gatewayId,
    page: PAGINATION.DEFAULT_PAGE,
    pageSize: PAGINATION.MAX_PAGE_SIZE,
  })

  if (error) return <ErrorState error={error} retry={() => void refetch()} />

  const columns: Column<Tag>[] = [
    { key: 'name', header: 'Name', accessor: (t) => t.name, sortable: true },
    {
      key: 'address',
      header: 'Address',
      accessor: (t) => <span className="font-mono text-xs">{t.address}</span>,
    },
    { key: 'dataType', header: 'Type', accessor: (t) => t.dataType },
    { key: 'unit', header: 'Unit', accessor: (t) => t.unit ?? '—' },
    {
      key: 'enabled',
      header: 'Enabled',
      accessor: (t) => (
        <Badge variant={t.enabled ? 'success' : 'default'}>{t.enabled ? 'Yes' : 'No'}</Badge>
      ),
    },
  ]

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={() => navigate(`${ROUTES.TAGS}?gatewayId=${gatewayId}`)}
        className="flex items-center gap-1.5 text-xs text-accent hover:underline"
      >
        Open in Tags <ExternalLink size={12} />
      </button>
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        rowKey={(t) => t.id}
        emptyMessage="No tags on this gateway."
      />
    </div>
  )
}

function ActivityTab() {
  // tap-api does not expose gateway message-rate history yet; the chart renders
  // its empty state. Wire to a metrics endpoint when one exists.
  return (
    <Card padding="md">
      <div className="mb-3 text-sm font-medium text-text-secondary">Message rate</div>
      <AreaChart
        data={[]}
        series={[{ dataKey: 'rate', label: 'Messages/min' }]}
        height={280}
        xAxisKey="time"
        emptyMessage="No activity metrics available from this gateway yet."
      />
    </Card>
  )
}
